import { ExternalLink, PlayCircle } from "lucide-react";
import { useLocale, useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";

type VideoSource =
  | { kind: "youtube"; id: string }
  | { kind: "file"; src: string }
  | { kind: "external"; href: string; host: string };

export function parseVideoUrl(url: string | null | undefined): VideoSource | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return { kind: "youtube", id: u.pathname.slice(1) };
    if (host.endsWith("youtube.com")) {
      const id = u.searchParams.get("v") ?? u.pathname.match(/\/(shorts|embed)\/([^/?]+)/)?.[2];
      if (id) return { kind: "youtube", id };
    }
    if (/\.(mp4|webm|mov|m4v)$/i.test(u.pathname)) return { kind: "file", src: url };
    return { kind: "external", href: url, host };
  } catch {
    return null;
  }
}

/** MuscleWiki serves English at /exercise/… and other languages under a prefix like /de-de/exercise/…. */
function localizeMuscleWiki(href: string, locale: string): string {
  const u = new URL(href);
  if (!u.hostname.endsWith("musclewiki.com")) return href;
  const path = u.pathname.replace(/^\/[a-z]{2}-[a-z]{2}(?=\/)/, "");
  u.pathname = locale === "en" ? path : `/${locale}-${locale}${path}`;
  return u.toString();
}

/** Embeds YouTube/MP4 videos; other pages (e.g. MuscleWiki blocks iframes) open in a new tab. */
export function ExerciseVideo({ url, title }: { url: string | null; title: string }) {
  const t = useTranslations("exercises");
  const locale = useLocale();
  const source = parseVideoUrl(url);
  if (!source) {
    return <p className="text-sm text-muted-foreground">{t("noVideo")}</p>;
  }
  if (source.kind === "youtube") {
    return (
      <div className="aspect-video overflow-hidden rounded-2xl bg-black">
        <iframe
          className="size-full"
          src={`https://www.youtube-nocookie.com/embed/${source.id}?rel=0&playsinline=1`}
          title={title}
          allow="accelerometer; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (source.kind === "file") {
    return (
      <video
        className="aspect-video w-full rounded-2xl bg-black object-contain"
        src={source.src}
        controls
        loop
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  return (
    <div className="rounded-2xl border border-dashed bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        <PlayCircle className="mt-0.5 size-8 shrink-0 text-primary" />
        <p className="text-sm text-muted-foreground">{t("externalHint", { host: source.host })}</p>
      </div>
      <Button asChild className="mt-3 w-full" size="lg">
        <a href={localizeMuscleWiki(source.href, locale)} target="_blank" rel="noopener noreferrer">
          {t("openExternal")} <ExternalLink />
        </a>
      </Button>
    </div>
  );
}
